<script setup lang="ts">
/**
 * The generator showcase on /ai-cover-letter: the input/result pair as a Sofax
 * field next to a raised panel, the four benefits as round-tile list items and
 * a stat row like the trust strip.
 *
 * It no longer opens with its own section heading or closes with its own call
 * to action. The page is now a guide first, and MarketingCoverLetterGuide ends
 * with the heading that introduces this block — two headings in a row read as
 * two pages stitched together.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()
const { stats, formatCount } = usePublicStats()

const features = computed(() => [
  {
    id: 1,
    icon: 'bi-person-check',
    title: t('homepage.featureOverview.aiCoverLetter.features.personalized.title'),
    description: t('homepage.featureOverview.aiCoverLetter.features.personalized.description'),
  },
  {
    id: 2,
    icon: 'bi-lightning-charge',
    title: t('homepage.featureOverview.aiCoverLetter.features.instant.title'),
    description: t('homepage.featureOverview.aiCoverLetter.features.instant.description'),
  },
  {
    id: 3,
    icon: 'bi-chat-quote',
    title: t('homepage.featureOverview.aiCoverLetter.features.professional.title'),
    description: t('homepage.featureOverview.aiCoverLetter.features.professional.description'),
  },
  {
    id: 4,
    icon: 'bi-bullseye',
    title: t('homepage.featureOverview.aiCoverLetter.features.matching.title'),
    description: t('homepage.featureOverview.aiCoverLetter.features.matching.description'),
  },
])

const paragraphs = ['paragraph1', 'paragraph2', 'paragraph3', 'closing'] as const

/** Only verifiable facts and the live counter; the fallback is the Premium promise. */
const statTiles = computed(() => [
  { id: 'free', value: '1', label: t('homepage.featureOverview.aiCoverLetter.stats.freeLetter') },
  { id: 'time', value: t('homepage.featureOverview.aiCoverLetter.stats.timeValue'), label: t('homepage.featureOverview.aiCoverLetter.stats.time') },
  stats.value?.coverLetters
    ? { id: 'generated', value: formatCount(stats.value.coverLetters), label: t('homepage.featureOverview.aiCoverLetter.stats.generated') }
    : { id: 'unlimited', value: '∞', label: t('homepage.featureOverview.aiCoverLetter.stats.unlimitedPremium') },
])
</script>

<template>
  <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell relative z-10">
        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Showcase: job description in, cover letter out -->
          <div class="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 xl:gap-[3.75rem]">
            <div class="flex flex-col gap-10">
              <div class="card p-[1.875rem]">
                <div class="display-heading display-heading-4 mb-6">
                  {{ t('homepage.featureOverview.aiCoverLetter.showcase.inputTitle') }}
                </div>
                <span class="field-label">{{ t('homepage.featureOverview.aiCoverLetter.showcase.jobDescription') }}</span>
                <div class="field-textarea min-h-0 !py-5 text-base leading-relaxed">
                  {{ t('homepage.featureOverview.aiCoverLetter.showcase.exampleJob') }}
                </div>
                <div class="mt-6 flex items-center gap-4">
                  <SofaxIconTile icon="bi-stars" round size="sm" />
                  <span class="text-base font-semibold text-accent">
                    {{ t('homepage.featureOverview.aiCoverLetter.showcase.aiProcessing') }}
                  </span>
                </div>
              </div>

              <!-- Benefits as the template's round-tile list -->
              <ul class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <li v-for="feature in features" :key="feature.id" class="flex items-start gap-4">
                  <SofaxIconTile :icon="feature.icon" round size="sm" />
                  <div class="flex-1">
                    <div class="display-heading display-heading-5 mb-2">{{ feature.title }}</div>
                    <p class="text-base">{{ feature.description }}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div class="relative">
              <div class="panel">
                <div class="display-heading display-heading-5 mb-6">
                  {{ t('homepage.featureOverview.aiCoverLetter.showcase.resultTitle') }}
                </div>

                <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <dt class="text-sm font-semibold text-muted dark:text-gray-400">{{ t('coverLetter.companyName') }}</dt>
                    <dd class="font-title font-bold text-ink dark:text-white">TechCorp</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-semibold text-muted dark:text-gray-400">{{ t('coverLetter.positionTitle') }}</dt>
                    <dd class="font-title font-bold text-ink dark:text-white">Frontend Developer</dd>
                  </div>
                </dl>

                <div class="my-6 h-px bg-line dark:bg-gray-700" />

                <div class="flex flex-col gap-4 text-base">
                  <p v-for="paragraph in paragraphs" :key="paragraph">
                    {{ t(`homepage.featureOverview.aiCoverLetter.showcase.exampleContent.${paragraph}`) }}
                  </p>
                </div>

                <div class="mt-8 flex items-center gap-4 rounded-[0.625rem] bg-ink p-5 text-white dark:bg-gray-900">
                  <SofaxIconTile icon="bi-stars" round size="sm" />
                  <div class="display-heading display-heading-5 !text-white">
                    {{ t('homepage.featureOverview.aiCoverLetter.showcase.powered') }}
                  </div>
                </div>
              </div>
              <img
                src="/sofax/content-shape-1.svg"
                alt=""
                aria-hidden="true"
                width="125"
                height="137"
                class="pointer-events-none absolute -right-12 -top-12 -z-10 hidden xl:inline-block"
              >
            </div>
          </div>

          <!-- Stat row, styled like the trust strip -->
          <dl class="grid grid-cols-1 gap-8 border-y border-line py-10 text-center sm:grid-cols-3 dark:border-gray-800">
            <div v-for="tile in statTiles" :key="tile.id">
              <dd class="font-title text-4xl font-bold -tracking-[1px] text-ink md:text-5xl dark:text-white">{{ tile.value }}</dd>
              <dt class="mt-2 text-sm text-muted dark:text-gray-400">{{ tile.label }}</dt>
            </div>
          </dl>

          <!--
            One button, no second headline: the guide above already made the
            case, and SofaxPageCta closes the page.
          -->
          <div class="mx-auto max-w-2xl text-center">
            <SofaxBtn
              :to="isAuthenticated ? '/cv' : '/registration'"
              :label="t('coverLetterGuide.product.cta')"
              variant="accent"
            />
          </div>
        </div>
      </div>

      <img
        src="/sofax/hero-1-element-1.svg"
        alt=""
        aria-hidden="true"
        width="291"
        height="200"
        class="pointer-events-none absolute -left-16 top-32 -z-10 hidden opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
